import { useRef, useState } from "react";
import {
  apiUpdateMe,
  apiChangePassword,
  apiDeleteMe,
  type ApiUser,
} from "../data/api";

import "./ProfileDialog.scss";

interface ProfileDialogProps {
  user: ApiUser;
  onClose: () => void;
  onUserUpdated: (user: ApiUser) => void;
  onLogout: () => void;
  onAccountDeleted: () => void;
}

type Tab = "profile" | "security" | "danger";

const MAX_AVATAR_BYTES = 2 * 1024 * 1024; // 2 MB

// Read an image file and downscale + re-encode it to a max edge size of 256px (JPEG).
async function imageFileToDataUrl(file: File): Promise<string> {
  if (file.size > MAX_AVATAR_BYTES) {
    throw new Error("A imagem precisa ter no máximo 2 MB");
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error || new Error("Falha ao ler imagem"));
    reader.readAsDataURL(file);
  });

  // Downscale to a square 256x256 to keep payload small
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const size = 256;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      const scale = Math.max(size / img.width, size / img.height);
      const dw = img.width * scale;
      const dh = img.height * scale;
      const dx = (size - dw) / 2;
      const dy = (size - dh) / 2;
      ctx.drawImage(img, dx, dy, dw, dh);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => reject(new Error("Imagem inválida"));
    img.src = dataUrl;
  });
}

export function getUserInitials(user: ApiUser | null | undefined): string {
  if (!user) return "?";
  const source = (user.name || user.email || "").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const ProfileDialog = ({
  user,
  onClose,
  onUserUpdated,
  onLogout,
  onAccountDeleted,
}: ProfileDialogProps) => {
  const [tab, setTab] = useState<Tab>("profile");

  // Profile tab
  const [name, setName] = useState(user.name || "");
  const [avatar, setAvatar] = useState<string | null>(user.avatar || null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState(false);

  // Security tab
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Danger tab
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteAck, setDeleteAck] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfileError(null);
    try {
      const dataUrl = await imageFileToDataUrl(file);
      setAvatar(dataUrl);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : "Erro ao processar imagem");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileError(null);
    setProfileSuccess(false);
    try {
      const updated = await apiUpdateMe({
        name: name.trim() || null,
        avatar,
      });
      onUserUpdated(updated);
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 2200);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword.length < 6) {
      setPasswordError("A nova senha precisa ter ao menos 6 caracteres");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("As senhas não coincidem");
      return;
    }

    setPasswordSaving(true);
    try {
      await apiChangePassword(currentPassword, newPassword);
      setPasswordSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => setPasswordSuccess(false), 2500);
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : "Erro ao trocar senha");
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleteError(null);
    if (!deleteAck) {
      setDeleteError("Marque a confirmação para prosseguir");
      return;
    }
    if (!deletePassword) {
      setDeleteError("Informe sua senha para confirmar");
      return;
    }
    setDeleting(true);
    try {
      await apiDeleteMe(deletePassword);
      onAccountDeleted();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Erro ao excluir conta");
      setDeleting(false);
    }
  };

  return (
    <div className="profile-overlay" onClick={onClose}>
      <div className="profile-dialog" onClick={(e) => e.stopPropagation()}>
        <button className="profile-dialog__close" onClick={onClose} aria-label="Fechar">
          &times;
        </button>

        <header className="profile-dialog__header">
          <div className="profile-dialog__avatar-big">
            {avatar ? (
              <img src={avatar} alt={user.name || user.email} />
            ) : (
              <span>{getUserInitials(user)}</span>
            )}
          </div>
          <div className="profile-dialog__heading">
            <h2>{user.name || user.email}</h2>
            <p>{user.email}</p>
          </div>
        </header>

        <nav className="profile-dialog__tabs">
          <button
            className={tab === "profile" ? "active" : ""}
            onClick={() => setTab("profile")}
          >
            Perfil
          </button>
          <button
            className={tab === "security" ? "active" : ""}
            onClick={() => setTab("security")}
          >
            Senha
          </button>
          <button
            className={tab === "danger" ? "active danger" : "danger"}
            onClick={() => setTab("danger")}
          >
            Excluir conta
          </button>
        </nav>

        {tab === "profile" && (
          <section className="profile-dialog__body">
            <div className="profile-dialog__field">
              <label>Foto de perfil</label>
              <div className="profile-dialog__avatar-edit">
                <div className="profile-dialog__avatar-preview">
                  {avatar ? (
                    <img src={avatar} alt="preview" />
                  ) : (
                    <span>{getUserInitials(user)}</span>
                  )}
                </div>
                <div className="profile-dialog__avatar-buttons">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    style={{ display: "none" }}
                  />
                  <button
                    type="button"
                    className="profile-dialog__btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Escolher imagem
                  </button>
                  {avatar && (
                    <button
                      type="button"
                      className="profile-dialog__btn profile-dialog__btn--ghost"
                      onClick={() => setAvatar(null)}
                    >
                      Remover
                    </button>
                  )}
                </div>
              </div>
              <span className="profile-dialog__hint">
                JPG ou PNG, máximo 2 MB. A imagem será reduzida automaticamente.
              </span>
            </div>

            <div className="profile-dialog__field">
              <label htmlFor="profile-name">Nome</label>
              <input
                id="profile-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome"
                maxLength={100}
              />
            </div>

            <div className="profile-dialog__field">
              <label>Email</label>
              <input type="email" value={user.email} disabled />
            </div>

            {profileError && (
              <div className="profile-dialog__error">{profileError}</div>
            )}
            {profileSuccess && (
              <div className="profile-dialog__success">Salvo!</div>
            )}

            <div className="profile-dialog__actions">
              <button
                className="profile-dialog__btn profile-dialog__btn--ghost"
                onClick={onLogout}
              >
                Sair da conta
              </button>
              <button
                className="profile-dialog__btn profile-dialog__btn--primary"
                onClick={handleSaveProfile}
                disabled={profileSaving}
              >
                {profileSaving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </section>
        )}

        {tab === "security" && (
          <section className="profile-dialog__body">
            <div className="profile-dialog__field">
              <label htmlFor="cur-pw">Senha atual</label>
              <input
                id="cur-pw"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="profile-dialog__field">
              <label htmlFor="new-pw">Nova senha</label>
              <input
                id="new-pw"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
              <span className="profile-dialog__hint">Mínimo de 6 caracteres.</span>
            </div>
            <div className="profile-dialog__field">
              <label htmlFor="confirm-pw">Confirmar nova senha</label>
              <input
                id="confirm-pw"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>

            {passwordError && (
              <div className="profile-dialog__error">{passwordError}</div>
            )}
            {passwordSuccess && (
              <div className="profile-dialog__success">Senha atualizada!</div>
            )}

            <div className="profile-dialog__actions">
              <button
                className="profile-dialog__btn profile-dialog__btn--primary"
                onClick={handleChangePassword}
                disabled={passwordSaving || !currentPassword || !newPassword || !confirmPassword}
              >
                {passwordSaving ? "Atualizando..." : "Atualizar senha"}
              </button>
            </div>
          </section>
        )}

        {tab === "danger" && (
          <section className="profile-dialog__body">
            <div className="profile-dialog__warn">
              <strong>Atenção:</strong> ao excluir sua conta, todos os seus quadros,
              pastas, tags e arquivos serão removidos <strong>permanentemente</strong>.
              Essa ação não pode ser desfeita.
            </div>

            <div className="profile-dialog__field">
              <label htmlFor="del-pw">Confirme com sua senha</label>
              <input
                id="del-pw"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>

            <label className="profile-dialog__check">
              <input
                type="checkbox"
                checked={deleteAck}
                onChange={(e) => setDeleteAck(e.target.checked)}
              />
              <span>
                Entendo que ao excluir minha conta perderei todo o meu conteúdo de forma
                irreversível.
              </span>
            </label>

            {deleteError && (
              <div className="profile-dialog__error">{deleteError}</div>
            )}

            <div className="profile-dialog__actions">
              <button
                className="profile-dialog__btn profile-dialog__btn--danger"
                onClick={handleDelete}
                disabled={deleting || !deleteAck || !deletePassword}
              >
                {deleting ? "Excluindo..." : "Excluir minha conta permanentemente"}
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
